import { useState, useEffect, useCallback, useRef } from 'react';
import { GLOBAL_CHAT_ROOM } from '../constants';
import { REACTION_PREFIX } from '../components/GameReactions';
import { filterProfanity } from '../utils/profanityFilter';
import {
  sendMessage, getMessageHistory, subscribeToMessages,
} from '../utils/supabaseActions';

/**
 * useChat — handles global chat, room chat, and game reactions.
 *
 * @param {string|null} currentUser
 * @param {string} roomCode
 * @param {string} screen
 * @param {Function} addToast
 * @param {Function} t — translation helper
 */
export function useChat(currentUser, roomCode, screen, addToast, t, chatSoundsEnabled = false) {
  // Room chat state
  const [messages, setMessages] = useState([]);
  const [messageInput, setMessageInput] = useState('');
  const [unreadChatCount, setUnreadChatCount] = useState(0);

  // Global chat state
  const [globalMessages, setGlobalMessages] = useState([]);
  const [globalMessageInput, setGlobalMessageInput] = useState('');
  const [globalUnreadCount, setGlobalUnreadCount] = useState(0);
  const [activeChatTab, setActiveChatTab] = useState('global');
  const [isChatWidgetOpen, setIsChatWidgetOpen] = useState(false);
  const audioContextRef = useRef(null);

  // Filter reactions out of room messages for normal chat display
  const roomChatMessages = messages.filter(m => !m.message?.startsWith(REACTION_PREFIX));
  const isGlobalChatVisible = screen === 'create-or-join'
    ? activeChatTab === 'global'
    : isChatWidgetOpen && activeChatTab === 'global';
  const isRoomChatVisible = (screen === 'room' || screen === 'game')
    && isChatWidgetOpen
    && activeChatTab === 'room';

  const playIncomingMessageSound = useCallback(async () => {
    if (!chatSoundsEnabled || typeof window === 'undefined') return;

    const AudioContextCtor = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextCtor) return;

    try {
      if (!audioContextRef.current) {
        audioContextRef.current = new AudioContextCtor();
      }
      const audioContext = audioContextRef.current;
      if (audioContext.state === 'suspended') {
        await audioContext.resume();
      }
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      oscillator.frequency.value = 880;
      oscillator.type = 'sine';
      gainNode.gain.setValueAtTime(0.05, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.001, audioContext.currentTime + 0.16);
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.16);
    } catch {
      // Browsers may block audio until the first user gesture.
    }
  }, [chatSoundsEnabled]);

  // ─── Global chat: subscribe while logged in ───
  useEffect(() => {
    if (!currentUser) return;

    getMessageHistory(GLOBAL_CHAT_ROOM, 50)
      .then(setGlobalMessages)
      .catch(() => addToast(t('errLoadGlobalChat'), 'error'));

    const unsubscribe = subscribeToMessages(GLOBAL_CHAT_ROOM, (newMsg) => {
      setGlobalMessages(prev => [...prev, newMsg]);
      if (newMsg.username !== currentUser && !isGlobalChatVisible) {
        setGlobalUnreadCount(prev => prev + 1);
        playIncomingMessageSound();
      }
    });

    return () => unsubscribe();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, isGlobalChatVisible, playIncomingMessageSound]);

  // Switch active tab based on room presence
  useEffect(() => {
    if (!roomCode) {
      setActiveChatTab('global');
    } else {
      setActiveChatTab('room');
    }
  }, [roomCode]);

  // Load message history when entering room
  useEffect(() => {
    if (roomCode && (screen === 'room' || screen === 'game')) {
      getMessageHistory(roomCode)
        .then(setMessages)
        .catch(() => addToast(t('errLoadMessages'), 'error'));
    }
  }, [roomCode, screen, addToast, t]);

  // Subscribe to new messages in room
  useEffect(() => {
    if (roomCode && (screen === 'room' || screen === 'game')) {
      const unsubscribe = subscribeToMessages(roomCode, (newMessage) => {
        setMessages(prev => [...prev, newMessage]);
        if (newMessage.username !== currentUser && !newMessage.message.startsWith('[REACTION]') && !isRoomChatVisible) {
          setUnreadChatCount(prev => prev + 1);
          playIncomingMessageSound();
        }
      });

      return () => unsubscribe();
    }
  }, [roomCode, screen, currentUser, isRoomChatVisible, playIncomingMessageSound]);

  // ─── Handlers ───

  const handleSendMessage = useCallback(async () => {
    const text = filterProfanity(messageInput.trim());
    if (!text || !roomCode) return;
    try {
      await sendMessage(roomCode, currentUser, text);
      setMessageInput('');
    } catch (e) {
      if (e.code === 'RATE_LIMIT') {
        const msg = e.seconds
          ? t('errChatRateLimitSeconds').replace('{n}', e.seconds)
          : t('errChatRateLimit');
        addToast(msg, 'error');
      } else {
        console.error('Failed to send message:', e);
        addToast(t('errSendMessage'), 'error');
      }
    }
  }, [messageInput, roomCode, currentUser, addToast, t]);

  const handleSendGlobalMessage = useCallback(async () => {
    const text = filterProfanity(globalMessageInput.trim());
    if (!text) return;
    try {
      await sendMessage(GLOBAL_CHAT_ROOM, currentUser, text);
      setGlobalMessageInput('');
    } catch (e) {
      if (e.code === 'RATE_LIMIT') {
        const msg = e.seconds
          ? t('errChatRateLimitSeconds').replace('{n}', e.seconds)
          : t('errChatRateLimit');
        addToast(msg, 'error');
      } else {
        addToast(t('errSendMessage'), 'error');
      }
    }
  }, [globalMessageInput, currentUser, addToast, t]);

  const handleSendReaction = useCallback(async (reactionText) => {
    if (!roomCode) return;
    try {
      await sendMessage(roomCode, currentUser, reactionText);
    } catch (e) {
      console.error('Failed to send reaction:', e);
    }
  }, [roomCode, currentUser]);

  /** Clear room chat state (call on room leave). */
  const clearRoomChat = useCallback(() => {
    setMessages([]);
    setUnreadChatCount(0);
  }, []);

  return {
    // Room chat
    messages, roomChatMessages,
    messageInput, setMessageInput,
    unreadChatCount, setUnreadChatCount,
    handleSendMessage,
    handleSendReaction,
    clearRoomChat,
    // Global chat
    globalMessages,
    globalMessageInput, setGlobalMessageInput,
    globalUnreadCount, setGlobalUnreadCount,
    handleSendGlobalMessage,
    // Widget state
    activeChatTab, setActiveChatTab,
    isChatWidgetOpen, setIsChatWidgetOpen,
  };
}
